import React, {useState} from "react";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import {Avatar, Stack, Tooltip, Typography} from "@mui/material";
import {DataGrid, esES} from "@mui/x-data-grid";
import {
    ClientFields,
    ClientType,
    CoOwnerRelationshipWithoutSolicitation,
    CoOwnerRelationshipWithoutSolicitationFields
} from "src/types/client/clientData";
import {SolicitationClient, SolicitationClientFields} from "../../../../../types/solicitation/solicitationData";
import {DeleteIconButton, EditIconButton} from "../../../../../components/buttons/Buttons";
import {dateFormatter} from "../../../../../utils/formatters";
import {EntityWithIdFields} from "../../../../../types/baseEntities";
import {numberFormatter} from "../../../../../utils/formatters/numberFormatter";
import {AddressFormatter} from "../../../../../utils/formatters/addressFormatter";
import {stringFormatter} from "../../../../../utils/formatters/stringFormatter";
import {MaritalStates} from "../../../../../types/generalData";


interface CoOwnersDataGridProps {
    relationships?: SolicitationClient[],
    onEdit?: (solicitationClient: SolicitationClient) => void,
    onDelete?: (solicitationClientId: number) => void
}


const CoOwnersDataGrid = ({relationships, onEdit, onDelete}: CoOwnersDataGridProps) => {
    const [pageSize, setPageSize] = useState<number>(5)

    const cols = [
        {
            flex: 0.16,
            field: 'descClienteTipo',
            headerName: 'Cotitular',
            renderCell: (params: any) => (
                <Stack direction={'row'} spacing={4} alignItems={'center'}>
                    {params.row[SolicitationClientFields.ClientTypeDesc] === 'Fisica' ? <Avatar><PersonIcon /></Avatar> : <Avatar><BusinessIcon /></Avatar>}
                    <Stack spacing={1}>
                        <Typography fontWeight={600} fontSize={14}>{params.row[SolicitationClientFields.BusinessName]}</Typography>
                        <Typography fontSize={12} color={'grey'} fontWeight={500}>
                            {numberFormatter.toCuitString(params.row[SolicitationClientFields.CUIT])}
                        </Typography>
                    </Stack>
                </Stack>
            )
        },
        {
          flex: 0.06,
          minWidth: 90,
          field: 'descClienteRelacionTipo',
          headerName: 'Tipo',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{params.row[SolicitationClientFields.RelationshipTypeDesc]}</Typography>
            )
        },
        {
            flex: 0.09,
            minWidth: 90,
            field: SolicitationClientFields.BirthDate,
            headerName: 'Fecha de Nacimiento',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{dateFormatter.toShortDate(params.row[SolicitationClientFields.BirthDate])}</Typography>
            )
        },
        {
            flex: 0.07,
            minWidth: 110,
            field: SolicitationClientFields.MaritalStatusDesc,
            headerName: 'Estado Civil',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{params.row[SolicitationClientFields.MaritalStatusDesc]}</Typography>
            )
        },
        {
            flex: 0.14,
            field: SolicitationClientFields.FianceName,
            minWidth: 125,
            headerName: 'Razón Social/Nombre (Conyuge)',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{(params.row[SolicitationClientFields.MaritalStatusCode] !== MaritalStates.Casado &&
                    params.row[SolicitationClientFields.MaritalStatusCode] !== MaritalStates.Convivientes) ? '-' :
                    params.row[SolicitationClientFields.FianceName]}</Typography>
            )
        },
        {
            flex: 0.18,
            field: SolicitationClientFields.Address,
            minWidth: 125,
            headerName: 'Dirección',
            renderCell: (params: any) => (
                <div>
                    <Tooltip title={params.row[SolicitationClientFields.Address]}>
                        <Typography fontSize={13}>{stringFormatter.cutIfHaveMoreThan(params.row[SolicitationClientFields.Address], 55)}</Typography>
                    </Tooltip>
                </div>
            )
        },
        {
            flex: 0.055,
            minWidth: 80,
            field: 'acciones',
            headerName: 'Acciones',
            renderCell: (params: any) => (
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                    <EditIconButton onClick={() => onEdit && onEdit(params.row as SolicitationClient)} tooltipTitle={'Editar'}/>
                    <DeleteIconButton onClick={() => onDelete && onDelete(params.row[EntityWithIdFields.Id])} tooltipTitle={'Eliminar'}/>
                </Stack>
            )
        }
    ]
    
    const colsView = [
        {
            flex: 0.2,
            field: 'descClienteTipo',
            headerName: 'Cotitular',
            renderCell: (params: any) => (
                <Stack direction={'row'} spacing={4} alignItems={'center'}>
                    {params.row[SolicitationClientFields.ClientTypeDesc] === 'Fisica' ? <Avatar><PersonIcon /></Avatar> : <Avatar><BusinessIcon /></Avatar>}
                    <Stack spacing={1}>
                        <Typography fontWeight={600} fontSize={14}>{params.row[SolicitationClientFields.BusinessName]}</Typography>
                        <Typography fontSize={12} color={'grey'} fontWeight={500}>{params.row[SolicitationClientFields.CUIT]}</Typography>
                    </Stack>
                </Stack>
            )
        },
        {
            flex: 0.11,
            minWidth: 90,
            field: SolicitationClientFields.BirthDate,
            headerName: 'Fecha de Nacimiento',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{dateFormatter.toShortDate(params.row[SolicitationClientFields.BirthDate])}</Typography>
            )
        },
        {
            flex: 0.07,
            minWidth: 110,
            field: SolicitationClientFields.MaritalStatusDesc,
            headerName: 'Estado Civil',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{params.row[SolicitationClientFields.MaritalStatusDesc]}</Typography>
            )
        },
        {
            flex: 0.14,
            field: SolicitationClientFields.FianceName,
            minWidth: 125,
            headerName: 'Razón Social/Nombre (Conyuge)',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{(params.row[SolicitationClientFields.MaritalStatusCode] !== MaritalStates.Casado &&
                    params.row[SolicitationClientFields.MaritalStatusCode] !== MaritalStates.Convivientes) ? '-' :
                    params.row[SolicitationClientFields.FianceName]}</Typography>
            )
        },
        {
            flex: 0.18,
            field: SolicitationClientFields.Address,
            minWidth: 125,
            headerName: 'Dirección',
            renderCell: (params: any) => (
                <div>
                    <Tooltip title={params.row[SolicitationClientFields.Address]}>
                        <Typography fontSize={13}>{stringFormatter.cutIfHaveMoreThan(params.row[SolicitationClientFields.Address], 55)}</Typography>
                    </Tooltip>
                </div>
            )
        },
    ]
    
    
    return (
        <>
            {
                relationships &&
                <DataGrid columns={(onEdit && onDelete) ? cols : colsView}
                          rows={relationships}
                          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                          autoHeight
                          rowsPerPageOptions={[5, 10, 25]}
                          pageSize={pageSize}
                          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                />
            }
        </>
    )
}


export default CoOwnersDataGrid

interface TempCoOwnersDataGridProps {
    relationships?: CoOwnerRelationshipWithoutSolicitation[],
    onEdit: (solicitationClient: CoOwnerRelationshipWithoutSolicitation) => void,
    onDelete: (clientId: number) => void
}

export const TempCoOwnersDataGrid = (props: TempCoOwnersDataGridProps) => {
    const [pageSize, setPageSize] = useState<number>(5)

    const getMaritalDesc = (code: number) => {
        if(code === MaritalStates.Casado) return 'Casado'
        if(code === MaritalStates.Divorciado) return 'Divorciado'
        if(code === MaritalStates.Separado) return 'Separado'
        if(code === MaritalStates.Viudo) return 'Viudo'
        if(code === MaritalStates.Soltero) return 'Soltero'
        if(code === MaritalStates.Convivientes) return 'Convivientes'
    }

    const cols = [
        {
            flex: 0.2,
            field: 'descClienteTipo',
            headerName: 'Cotitular',
            renderCell: (params: any) => (
                <Stack direction={'row'} spacing={4} alignItems={'center'}>
                    {params.row[ClientFields.ClientTypeCode] === ClientType.Fisica ? <Avatar><PersonIcon /></Avatar> : <Avatar><BusinessIcon /></Avatar>}
                    <Stack spacing={1}>
                        <Typography fontWeight={600} fontSize={14}>{params.row[ClientFields.BusinessName]}</Typography>
                        <Typography fontSize={12} color={'grey'} fontWeight={500}>
                            {numberFormatter.toCuitString(params.row[ClientFields.CUIT])}
                        </Typography>
                    </Stack>
                </Stack>
            )
        },
        {
            flex: 0.11,
            minWidth: 90,
            field: ClientFields.BirthDate,
            headerName: 'Fecha de Nacimiento',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{dateFormatter.toShortDate(params.row[ClientFields.BirthDate])}</Typography>
            )
        },
        {
            flex: 0.07,
            minWidth: 110,
            field: ClientFields.MaritalStatusCode,
            headerName: 'Estado Civil',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{getMaritalDesc(params.row[SolicitationClientFields.MaritalStatusCode])}</Typography>
            )
        },
        {
            flex: 0.14,
            field: CoOwnerRelationshipWithoutSolicitationFields.FianceName,
            minWidth: 125,
            headerName: 'Razón Social/Nombre (Conyuge)',
            renderCell: (params: any) => (
                <Typography fontSize={13}>{params.row[SolicitationClientFields.MaritalStatusCode] !== MaritalStates.Casado && params.row[SolicitationClientFields.MaritalStatusCode] !== MaritalStates.Convivientes ?
                    '-' : params.row[SolicitationClientFields.FianceName]}</Typography>
            )
        },
        {
            flex: 0.18,
            field: CoOwnerRelationshipWithoutSolicitationFields.Address,
            minWidth: 125,
            headerName: 'Dirección',
            renderCell: (params: any) => (
                <div>
                    <Tooltip title={AddressFormatter.toFullAddress(params.row[CoOwnerRelationshipWithoutSolicitationFields.Address])}>
                        <Typography fontSize={13}>
                            {stringFormatter.cutIfHaveMoreThan(AddressFormatter.toFullAddress(params.row[CoOwnerRelationshipWithoutSolicitationFields.Address]), 55)}
                        </Typography>
                    </Tooltip>
                </div>
            )
        },
        {
            flex: 0.055,
            minWidth: 80,
            field: 'acciones',
            headerName: 'Acciones',
            renderCell: (params: any) => (
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                    <EditIconButton onClick={() => props.onEdit(params.row as CoOwnerRelationshipWithoutSolicitation)} tooltipTitle={'Editar'}/>
                    <DeleteIconButton onClick={() => props.onDelete(params.row[EntityWithIdFields.Id])} tooltipTitle={'Eliminar'}/>
                </Stack>
            )
        }
    ]
    
    
    return (
        <>
            {
                props.relationships &&
                <DataGrid columns={cols}
                          rows={props.relationships}
                          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                          autoHeight
                          rowsPerPageOptions={[5, 10, 25]}
                          pageSize={pageSize}
                          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                />
            }
        </>
    )
}